import { Alert, Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { acceptInvitation, getInvitationDetails, type InvitationDetailsResponse } from "../api/client";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { useAuth } from "../contexts/AuthContext";

export function InvitationAccept() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [details, setDetails] = useState<InvitationDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expiresLabel = useMemo(() => {
    if (!details?.expires_at) return null;
    try {
      return new Date(details.expires_at).toLocaleString();
    } catch {
      return details.expires_at;
    }
  }, [details?.expires_at]);

  useEffect(() => {
    if (!token) {
      setError("Missing token");
      setLoading(false);
      return;
    }
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    getInvitationDetails(token, abort.signal)
      .then((res) => {
        if (abort.signal.aborted) return;
        setDetails(res);
      })
      .catch((e) => {
        if (abort.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Invalid or expired invitation");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoading(false);
      });
    return () => abort.abort();
  }, [token]);

  async function onAccept() {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await acceptInvitation(token);
      await auth.setTokens({ accessToken: res.access_token, refreshToken: res.refresh_token });
      navigate("/home", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Invitation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Accept your invitation to join a tenant in OpenMon.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          {loading ? (
            <PageLoadingState variant="inline" message="Loading invitation…" />
          ) : error ? (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Button component={RouterLink} to="/login" variant="contained">
                Go to sign in
              </Button>
            </Stack>
          ) : !details ? (
            <Alert severity="error">Invalid or expired invitation.</Alert>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                You were invited to <strong>{details.tenant.name}</strong> as <strong>{details.role}</strong>.
              </Alert>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Email: <span className="om-text-mono">{details.email}</span>
              </Typography>
              {expiresLabel ? (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Expires: {expiresLabel}
                </Typography>
              ) : null}

              <Button
                variant="contained"
                size="large"
                disabled={submitting || loading}
                onClick={onAccept}
              >
                {submitting ? "Accepting…" : "Accept invitation"}
              </Button>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}

